/**
 * Tiny module-level pub/sub for visualization edits.
 *
 * The lab chat watches assistant messages for `editVisualization` tool
 * completions. When one lands, the lab calls `bumpVisualizationVersion(id)`
 * which increments a counter keyed by visualizationId and notifies all
 * subscribers (currently: rendered Remotion videos, but anything that wants
 * to live-refresh on edit can subscribe).
 *
 * Why this instead of a React context: the publishers (lab-chat-view's
 * effect over messages) and subscribers (renderer components nested several
 * layers deep) don't naturally share a context boundary, and a context
 * change would re-render every consumer of the context unnecessarily. The
 * pub/sub is per-visualizationId so a render only re-renders when ITS
 * visualization gets edited.
 *
 * Single-tab in-memory only — refresh across tabs needs a real channel.
 */

type Listener = (version: number) => void;

const versions = new Map<string, number>();
const listeners = new Map<string, Set<Listener>>();

export function getVisualizationVersion(visualizationId: string): number {
  return versions.get(visualizationId) ?? 0;
}

/**
 * Notify subscribers that a visualization's saved source has changed.
 * Idempotent — calling with the same versionId twice still bumps the
 * counter and triggers a re-fetch (the server returns the same code, so
 * the consumer's render is a no-op via React's shallow check).
 */
export function bumpVisualizationVersion(visualizationId: string): number {
  const next = (versions.get(visualizationId) ?? 0) + 1;
  versions.set(visualizationId, next);
  const subs = listeners.get(visualizationId);
  if (subs) {
    for (const l of subs) l(next);
  }
  return next;
}

export function subscribeVisualizationVersion(
  visualizationId: string,
  listener: Listener,
): () => void {
  let bucket = listeners.get(visualizationId);
  if (!bucket) {
    bucket = new Set();
    listeners.set(visualizationId, bucket);
  }
  bucket.add(listener);
  return () => {
    bucket?.delete(listener);
    if (bucket?.size === 0) listeners.delete(visualizationId);
  };
}
